/**
 * Tic Tac Toe Game
 * Play against human or AI with multiple difficulty levels
 */

import React, { useState } from 'react';
import { useLocalStorage } from './hooks/useLocalStorage';
import useGameAI from './hooks/useGameAI';
import GameLayout from './common/GameLayout';
import ScoreDisplay from './common/ScoreDisplay';
import { GAME_CONFIG } from './config/gameConfig';

const TicTacToe = ({ onHome }) => {
  const [gameState, setGameState] = useState(GAME_CONFIG.states.MENU);
  const [score, setScore] = useLocalStorage('tictactoe_score', 0);
  const [highScore, setHighScore] = useLocalStorage('tictactoe_highScore', 0);
  const { getAIMove } = useGameAI();

  const [board, setBoard] = useState(Array(9).fill(null));
  const [isXNext, setIsXNext] = useState(true); // X is human, O is computer/player2
  const [winner, setWinner] = useState(null);
  const [moves, setMoves] = useState(0);
  const [gameMode, setGameMode] = useState(null); // 'human' or 'computer'
  const [difficulty, setDifficulty] = useState(null); // 'easy', 'medium', 'hard'
  const [gamePhase, setGamePhase] = useState('menu'); // menu, difficulty, playing, gameover
  const [message, setMessage] = useState('');

  // Calculate winner
  const calculateWinner = (squares) => {
    const lines = [
      [0, 1, 2], [3, 4, 5], [6, 7, 8], // Rows
      [0, 3, 6], [1, 4, 7], [2, 5, 8], // Columns
      [0, 4, 8], [2, 4, 6], // Diagonals
    ];
    for (let i = 0; i < lines.length; i++) {
      const [a, b, c] = lines[i];
      if (squares[a] && squares[a] === squares[b] && squares[a] === squares[c]) {
        return squares[a];
      }
    }
    return null;
  };

  // Check if board is full
  const isBoardFull = (boardState) => boardState.every((cell) => cell !== null);

  // AI Scoring function for Tic Tac Toe
  const scoreMove = (index, boardState, player) => {
    let score = 0;
    const testBoard = [...boardState];
    testBoard[index] = player;

    // Check if this move wins
    if (calculateWinner(testBoard) === player) {
      return 1000; // Highest priority
    }

    // Check if opponent can win next move
    const opponent = player === 'X' ? 'O' : 'X';
    const opponentBoard = [...boardState];
    opponentBoard[index] = opponent;
    if (calculateWinner(opponentBoard) === opponent) {
      return 100; // Block opponent
    }

    // Center control
    if (index === 4) score += 10;

    // Corner control
    if ([0, 2, 6, 8].includes(index)) score += 5;

    return score;
  };

  // Get AI move
  const getComputerMove = (boardState) => {
    const possibleMoves = [];
    for (let i = 0; i < 9; i++) {
      if (boardState[i] === null) {
        possibleMoves.push(i);
      }
    }

    if (possibleMoves.length === 0) return null;

    const scoreFunc = (index) => scoreMove(index, boardState, 'O');
    return getAIMove(difficulty, possibleMoves, scoreFunc);
  };

  // Handle cell click
  const handleClick = (index) => {
    if (board[index] || calculateWinner(board)) return;
    if (gameMode === 'computer' && !isXNext) return; // Block during computer turn

    const newBoard = [...board];
    newBoard[index] = 'X';
    setBoard(newBoard);
    setMoves(moves + 1);

    const gameWinner = calculateWinner(newBoard);
    if (gameWinner === 'X') {
      setWinner('X');
      setGameState(GAME_CONFIG.states.PLAYING);
      setGamePhase('gameover');
      setScore((prev) => prev + 100);
      setMessage('You Won! 🎉');
      if (moves + 1 > highScore) setHighScore(moves + 1);
      return;
    }

    if (isBoardFull(newBoard)) {
      setGameState(GAME_CONFIG.states.PLAYING);
      setGamePhase('gameover');
      setMessage('Draw! 🤝');
      return;
    }

    setIsXNext(false);

    // Computer turn
    if (gameMode === 'computer') {
      setTimeout(() => {
        const computerMove = getComputerMove(newBoard);
        if (computerMove !== null && computerMove !== undefined) {
          const computerBoard = [...newBoard];
          computerBoard[computerMove] = 'O';
          setBoard(computerBoard);
          setMoves(moves + 2);

          const computerWinner = calculateWinner(computerBoard);
          if (computerWinner === 'O') {
            setWinner('O');
            setGameState(GAME_CONFIG.states.PLAYING);
            setGamePhase('gameover');
            setMessage('Computer Won! 🤖');
            return;
          }

          if (isBoardFull(computerBoard)) {
            setGameState(GAME_CONFIG.states.PLAYING);
            setGamePhase('gameover');
            setMessage('Draw! 🤝');
            return;
          }

          setIsXNext(true);
        }
      }, 500);
    } else {
      setIsXNext(true);
    }
  };

  // Start game
  const startGame = () => {
    setBoard(Array(9).fill(null));
    setIsXNext(true);
    setWinner(null);
    setMoves(0);
    setGamePhase('playing');
    setGameState(GAME_CONFIG.states.PLAYING);
    setMessage(gameMode === 'computer' ? 'Your turn (X)' : 'X turn');
  };

  // Reset game
  const resetGame = () => {
    if (winner && winner === 'X' && moves > highScore) {
      setHighScore(moves);
    }
    setGameMode(null);
    setDifficulty(null);
    setGamePhase('menu');
    setGameState(GAME_CONFIG.states.MENU);
    setWinner(null);
  };

  const gameWinner = calculateWinner(board);
  const isFull = isBoardFull(board);

  return (
    <GameLayout
      title="Tic Tac Toe ❌"
      score={score}
      highScore={highScore}
      gameState={gameState}
      onRestart={resetGame}
      onHome={onHome}
    >
      <div style={styles.gameArea}>
        {/* Game Mode Selection */}
        {gamePhase === 'menu' && (
          <div style={styles.menuContainer}>
            <h2 style={styles.menuTitle}>Tic Tac Toe</h2>
            <p style={styles.menuDescription}>Choose your opponent</p>
            <div style={styles.modeButtonsContainer}>
              <button
                onClick={() => {
                  setGameMode('human');
                  startGame();
                }}
                className="btn btn-primary btn-lg"
              >
                👥 Human vs Human
              </button>
              <button
                onClick={() => {
                  setGameMode('computer');
                  setGamePhase('difficulty');
                }}
                className="btn btn-success btn-lg"
              >
                🤖 Human vs Computer
              </button>
            </div>
          </div>
        )}

        {/* Difficulty Selection */}
        {gamePhase === 'difficulty' && (
          <div style={styles.menuContainer}>
            <h2 style={styles.menuTitle}>Select Difficulty</h2>
            <div style={styles.difficultyButtonsContainer}>
              <button
                onClick={() => {
                  setDifficulty('easy');
                  startGame();
                }}
                className="btn btn-primary"
              >
                🟢 Easy
              </button>
              <button
                onClick={() => {
                  setDifficulty('medium');
                  startGame();
                }}
                className="btn btn-warning"
              >
                🟡 Medium
              </button>
              <button
                onClick={() => {
                  setDifficulty('hard');
                  startGame();
                }}
                className="btn btn-danger"
              >
                🔴 Hard (Unbeatable)
              </button>
            </div>
          </div>
        )}

        {/* Game Board */}
        {gamePhase === 'playing' && gameState === GAME_CONFIG.states.PLAYING && (
          <div style={styles.playingArea}>
            <div style={styles.gameInfo}>
              {gameWinner ? (
                <div style={styles.messageWin}>
                  {gameWinner === 'X' ? 'You Won! 🎉' : 'Computer Won! 🤖'}
                </div>
              ) : isFull ? (
                <div style={styles.messageDraw}>Draw! 🤝</div>
              ) : (
                <div style={styles.turnIndicator}>
                  {isXNext ? '❌ Your Turn' : '⭕ Computer Thinking...'}
                </div>
              )}
            </div>

            <div style={styles.boardContainer}>
              {board.map((value, index) => (
                <button
                  key={index}
                  onClick={() => handleClick(index)}
                  style={{
                    ...styles.cell,
                    backgroundColor: value ? '#667eea' : '#f0d9b5',
                    color: value === 'X' ? '#ffffff' : '#000000',
                    fontSize: value ? '32px' : '24px',
                    fontWeight: 'bold',
                    cursor: !gameWinner && !isFull && isXNext ? 'pointer' : 'default',
                  }}
                  disabled={!!gameWinner || isFull || !!value || !isXNext}
                >
                  {value}
                </button>
              ))}
            </div>

            {(gameWinner || isFull) && (
              <div style={styles.gameOverButtons}>
                <button
                  onClick={startGame}
                  className="btn btn-primary"
                >
                  🔄 Play Again
                </button>
                <button
                  onClick={resetGame}
                  className="btn btn-secondary"
                >
                  🏠 Main Menu
                </button>
              </div>
            )}

            {gameMode === 'computer' && difficulty && (
              <div style={styles.difficultyDisplay}>
                Difficulty: {difficulty.toUpperCase()}
              </div>
            )}
          </div>
        )}
      </div>
    </GameLayout>
  );
};

const styles = {
  gameArea: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1.5rem',
    width: '100%',
  },
  menuContainer: {
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    border: `2px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: GAME_CONFIG.radius.lg,
    padding: '3rem 2rem',
    textAlign: 'center',
    minWidth: '400px',
  },
  menuTitle: {
    color: GAME_CONFIG.colors.accent,
    fontSize: '2rem',
    marginBottom: '1rem',
    fontWeight: 'bold',
  },
  menuDescription: {
    color: GAME_CONFIG.colors.light,
    marginBottom: '2rem',
  },
  modeButtonsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  difficultyButtonsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  playingArea: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1.5rem',
  },
  gameInfo: {
    minHeight: '60px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  messageWin: {
    color: GAME_CONFIG.colors.success,
    fontSize: '1.5rem',
    fontWeight: 'bold',
  },
  messageDraw: {
    color: GAME_CONFIG.colors.accent2,
    fontSize: '1.5rem',
    fontWeight: 'bold',
  },
  turnIndicator: {
    color: GAME_CONFIG.colors.light,
    fontSize: '1.2rem',
    fontWeight: 'bold',
    padding: '1rem 2rem',
    backgroundColor: `rgba(0, 212, 255, 0.1)`,
    border: `1px solid ${GAME_CONFIG.colors.accent}`,
    borderRadius: GAME_CONFIG.radius.md,
  },
  boardContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 80px)',
    gap: '8px',
    padding: '1rem',
    backgroundColor: `rgba(0, 0, 0, 0.3)`,
    border: `2px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: GAME_CONFIG.radius.md,
  },
  cell: {
    width: '80px',
    height: '80px',
    border: `2px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      transform: 'scale(1.05)',
    },
  },
  gameOverButtons: {
    display: 'flex',
    gap: '1rem',
  },
  difficultyDisplay: {
    color: GAME_CONFIG.colors.light,
    fontSize: '0.9rem',
    backgroundColor: `rgba(0, 212, 255, 0.1)`,
    border: `1px solid ${GAME_CONFIG.colors.accent}`,
    borderRadius: GAME_CONFIG.radius.md,
    padding: '0.75rem 1.5rem',
  },
};

export default TicTacToe;
